/**
 * Wall patterns — the TWIN CRUSHERS (legacy Avoid! family `walls`;
 * docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * Owner spec: a wall attack is ALWAYS two walls crushing in from OPPOSITE sides —
 * either LEFT+RIGHT (`WALL_LR`) or TOP+BOTTOM (`WALL_TB`) — leaving a survivable lane
 * between them (a seeded-offset gap the player must reach). Each wall CREEPS in slowly
 * over the telegraph (the teaser — you see which way to dodge), then CRUSHES fast to
 * its closed position, HOLDS shut, and OPENS back out. Their spiked inner faces
 * (rendered by src/game/WallSpikeFx.ts) point at the lane and interlock when close.
 *
 * Each wall is a {@link import('../types').RectHazard} carrying creep→crush→open
 * KEYFRAMES (`crushAxis` + `restPos`/`creepPos`/`crushPos`); the world stepper drives
 * the motion as a pure function of time. Timings + the lane width come from
 * {@link import('../constants').WALL}.
 *
 * Coordinates are VIEWPORT-RELATIVE. NOTE: pure core module — never import Phaser,
 * React, or Firebase here.
 */
import { WALL } from '../constants';
import type { Hazard, RectHazard } from '../types';
import type { Pattern, PatternContext } from './types';

/** Lane edge margin (fraction of the span) so the gap never hugs an edge. */
const LANE_EDGE_MARGIN_FRACTION = 0.06;
/** How far (fraction of the span) the lane centre may jitter off dead-centre. */
const LANE_JITTER_FRACTION = 0.22;

/** Build one wall of a pair with its creep→crush→open keyframes along `crushAxis`. */
function makeWall(
  elapsedMs: number,
  telegraphMs: number,
  activeMs: number,
  x: number,
  y: number,
  w: number,
  h: number,
  crushAxis: 'x' | 'y',
  crushPos: number,
  restPos: number,
): RectHazard {
  const creepPos = restPos + WALL.creepFraction * (crushPos - restPos);
  return {
    id: 0,
    family: 'walls',
    kind: 'rect',
    bornMs: elapsedMs,
    telegraphMs,
    activeMs,
    x,
    y,
    w,
    h,
    crushAxis,
    restPos,
    creepPos,
    crushPos,
  };
}

/**
 * Build a twin crusher. `vertical` = top+bottom walls (crush along y); otherwise
 * left+right (crush along x). The survivable lane is `WALL.gapFraction` of the span,
 * its centre jittered from the seeded stream and clamped on-screen.
 */
function makeTwinCrusher(ctx: PatternContext, vertical: boolean): Hazard[] {
  const { viewport, difficulty, rng, elapsedMs } = ctx;
  const { width, height } = viewport;

  // Slow creep (telegraph teaser) then fast crush + closed hold + open (active).
  const telegraphMs = difficulty.attackSpeedMs * 1.5;
  const activeMs = difficulty.attackSpeedMs * 1.4;

  const span = vertical ? height : width;
  // Absolute lane width (same comfortable gap for both orientations), clamped so it
  // can't exceed the span on a tiny field.
  const gapW = Math.min(WALL.laneWidth, span * 0.6);
  const half = gapW / 2;
  const margin = span * LANE_EDGE_MARGIN_FRACTION;
  const minC = half + margin;
  const maxC = span - half - margin;
  const jitter = (rng() - 0.5) * span * LANE_JITTER_FRACTION;
  const c = span / 2 + jitter;
  const center = c < minC ? minC : c > maxC ? maxC : c;

  // The "low" wall covers [0, center-half]; the "high" wall covers [center+half, span].
  const loW = center - half;
  const hiStart = center + half;
  const hiW = span - hiStart;

  if (vertical) {
    const top = makeWall(elapsedMs, telegraphMs, activeMs, 0, -loW, width, loW, 'y', 0, -loW);
    const bottom = makeWall(elapsedMs, telegraphMs, activeMs, 0, height, width, hiW, 'y', hiStart, height);
    return [top, bottom];
  }
  const left = makeWall(elapsedMs, telegraphMs, activeMs, -loW, 0, loW, height, 'x', 0, -loW);
  const right = makeWall(elapsedMs, telegraphMs, activeMs, width, 0, hiW, height, 'x', hiStart, width);
  return [left, right];
}

/** Attack — twin crushers from the LEFT and RIGHT (dodge into the vertical lane). */
export const WALL_LR: Pattern = {
  id: 'WALL_LR',
  family: 'walls',
  spawn: (ctx) => makeTwinCrusher(ctx, false),
};

/** Attack — twin crushers from the TOP and BOTTOM (dodge into the horizontal lane). */
export const WALL_TB: Pattern = {
  id: 'WALL_TB',
  family: 'walls',
  spawn: (ctx) => makeTwinCrusher(ctx, true),
};
